import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { TalentTeamRow } from './talentMapShared';
import { isCompleted, isOnLicense } from '../../../../../shared/talent-map-stats';

interface Props {
  /** Filas del scope "Mi equipo": directos (isDirect) + estructura recursiva. */
  rows: TalentTeamRow[];
  onOpen: (email: string) => void;
}

/**
 * "¿Cómo voy?" para el líder, arriba del Mapa del equipo (pedido Sol 14/09:
 * "que todos los líderes puedan consultar en qué estado están").
 *
 * Dos números distintos a propósito: lo que TIENE que hacer (sus directos, con
 * la lista de quiénes le faltan y click directo a evaluar) y lo que puede
 * MIRAR (su estructura completa, que evalúan los líderes intermedios). Solo
 * cuentan como evaluados los confirmados; onboarding y licencia no aplican al
 * ciclo (Sol 18/09: "que dejen de figurar en los % de que falta completar").
 */
export function LeaderProgressCard({ rows, onOpen }: Props) {
  const { t } = useTranslation();

  const p = useMemo(() => {
    const applies = (r: TalentTeamRow) => r.eligibilityStatus !== 'onboarding' && !isOnLicense(r);
    const directs = rows.filter(r => r.isDirect !== false && applies(r));
    const pending = directs
      .filter(r => !isCompleted(r))
      .sort((a, b) => `${a.name} ${a.lastName}`.localeCompare(`${b.name} ${b.lastName}`, 'es'));
    const structure = rows.filter(applies);
    return {
      directsTotal: directs.length,
      directsDone: directs.length - pending.length,
      pending,
      structureTotal: structure.length,
      structureDone: structure.filter(isCompleted).length,
      hasIndirects: rows.some(r => r.isDirect === false),
    };
  }, [rows]);

  if (p.directsTotal === 0 && p.structureTotal === 0) return null;

  const pct = p.directsTotal > 0 ? Math.round((p.directsDone / p.directsTotal) * 100) : 0;
  const done = p.directsTotal > 0 && p.pending.length === 0;

  return (
    <Card className={cn(done && 'border-emerald-200 dark:border-emerald-900/50')}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold">{t('performance.talent_map.progress.title')}</h2>
          <span className="text-sm tabular-nums text-muted-foreground">
            {t('performance.talent_map.progress.directs', { done: p.directsDone, total: p.directsTotal })}
          </span>
        </div>

        {p.directsTotal > 0 && (
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', done ? 'bg-emerald-500' : 'bg-teal-600')}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-sm font-semibold tabular-nums shrink-0">{pct}%</span>
          </div>
        )}

        {done ? (
          <p className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {t('performance.talent_map.progress.all_done')}
          </p>
        ) : p.pending.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              {t('performance.talent_map.progress.pending', { count: p.pending.length })}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {p.pending.map(r => (
                <button
                  key={r.email}
                  type="button"
                  onClick={() => onOpen(r.email)}
                  className="inline-flex items-center gap-1 rounded-full border bg-background px-2.5 py-1 text-xs hover:bg-muted transition-colors"
                  title={t('performance.talent_map.progress.evaluate_hint', { name: r.name })}
                >
                  {r.name} {r.lastName}
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {p.hasIndirects && (
          <p className="text-xs text-muted-foreground border-t pt-2.5">
            {t('performance.talent_map.progress.structure', {
              done: p.structureDone,
              total: p.structureTotal,
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
